import { useTranslation } from "react-i18next"
import { BetterDocExchangeLink } from "@/modules/admin/better-doc/components/better-doc-exchange-link"
import { BetterDocPayloadButton } from "@/modules/admin/better-doc/components/better-doc-payload-button"
import {
  BetterDocTable,
  BetterDocTableCell,
  BetterDocTableHeaderCell,
} from "@/modules/admin/better-doc/components/better-doc-table"
import { formatBetterDocDateTime } from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocRecommendedMedic } from "@/modules/admin/better-doc/services/betterDocSessionQuery"

type BetterDocMedicTableProps = {
  medics: ReadonlyArray<BetterDocRecommendedMedic>
}

type BetterDocMedicColumn = {
  key: string
  header: string
  cell: (medic: BetterDocRecommendedMedic) => React.ReactNode
}

export const BetterDocMedicTable = ({ medics }: BetterDocMedicTableProps) => {
  const { t } = useTranslation()

  const columns: ReadonlyArray<BetterDocMedicColumn> = [
    {
      key: "position",
      header: t("admin.betterDoc.medicTable.position"),
      cell: (medic) => medic.position,
    },
    {
      key: "selectionId",
      header: t("admin.betterDoc.medicTable.selectionId"),
      cell: (medic) => medic.selectionId,
    },
    {
      key: "received",
      header: t("admin.betterDoc.medicTable.received"),
      cell: (medic) => (
        <div className="flex flex-col items-start">
          <span className="whitespace-nowrap">{formatBetterDocDateTime(medic.receivedAt)}</span>
          <BetterDocExchangeLink exchangeId={medic.receivedInExchangeId} />
        </div>
      ),
    },
    {
      key: "payload",
      header: t("admin.betterDoc.payload.title"),
      cell: (medic) => (
        <BetterDocPayloadButton
          payload={medic.payload}
          recordId={medic.recommendedMedicId}
          testId={`better-doc-admin-medic-payload-${medic.recommendedMedicId}`}
        />
      ),
    },
  ]

  if (medics.length === 0) {
    return (
      <p className="text-on-surface-subdued text-sm" data-testid="better-doc-admin-medic-table-empty">
        {t("admin.betterDoc.medicTable.empty")}
      </p>
    )
  }

  return (
    <BetterDocTable data-testid="better-doc-admin-medic-table">
      <thead>
        <tr>
          {columns.map((column) => (
            <BetterDocTableHeaderCell key={column.key}>{column.header}</BetterDocTableHeaderCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {medics.map((medic) => (
          <tr
            key={medic.recommendedMedicId}
            className="hover:bg-surface-hovered"
            data-testid="better-doc-admin-medic-row">
            {columns.map((column) => (
              <BetterDocTableCell key={column.key}>{column.cell(medic)}</BetterDocTableCell>
            ))}
          </tr>
        ))}
      </tbody>
    </BetterDocTable>
  )
}
